import React, { useState, useRef } from 'react';
import { X, Plus, Mail } from 'lucide-react';
import { Input } from './input';
import { Button } from './button';

/**
 * Componente para gestionar múltiples emails como tags
 * Uso: <EmailTagInput emails={emails} onChange={setEmails} />
 */
export function EmailTagInput({ 
  emails = [], 
  onChange, 
  placeholder = "Agregar email...",
  maxEmails = 10 
}) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email.toLowerCase());
  };

  const addEmail = () => {
    const email = inputValue.trim().toLowerCase();
    
    if (!email) {
      setError('');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Email inválido');
      return;
    }
    
    if (emails.includes(email)) {
      setError('Email ya agregado');
      return;
    }
    
    if (emails.length >= maxEmails) {
      setError(`Máximo ${maxEmails} emails`);
      return;
    }
    
    onChange([...emails, email]);
    setInputValue('');
    setError('');
    inputRef.current?.focus();
  };

  const removeEmail = (emailToRemove) => {
    onChange(emails.filter(e => e !== emailToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      removeEmail(emails[emails.length - 1]);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-white min-h-[42px]">
        {emails.map((email, idx) => (
          <span
            key={idx}
            className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded-full"
          >
            <Mail className="h-3 w-3" />
            {email}
            <button
              type="button"
              onClick={() => removeEmail(email)}
              className="hover:bg-blue-200 rounded-full p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <div className="flex-1 min-w-[200px] flex gap-1">
          <Input
            ref={inputRef}
            type="email"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setError('');
            }}
            onKeyDown={handleKeyDown}
            onBlur={addEmail}
            placeholder={emails.length === 0 ? placeholder : "Agregar otro..."}
            className="border-0 shadow-none focus-visible:ring-0 p-0 h-7"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={addEmail}
            className="h-7 px-2"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}
      <p className="text-xs text-gray-400">
        Presione Enter o click en + para agregar. {emails.length}/{maxEmails} emails.
      </p>
    </div>
  );
}

export default EmailTagInput;
